import { z } from "zod";
import { TaskPriority, TaskType } from "@common/types/task";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
} from "@common/types/taskTemplate";
import { OrganizationTaskTemplateSchema } from "@common/types/organizationTaskTemplate";
import {
    refineDeadlineAnchor,
    refineViewConfigForCreate,
    TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH,
    viewConfigValueSchema,
} from "@common/schemas/task-template.schema";

export const OrganizationTaskTemplatePathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationTaskTemplateIdParamsSchema = z.object({
    templateId: z.string().uuid(),
});

export const ORGANIZATION_TASK_TEMPLATE_LIST_DEFAULT_LIMIT = 50;
export const ORGANIZATION_TASK_TEMPLATE_LIST_MAX_LIMIT = 200;
export const ORGANIZATION_TASK_TEMPLATE_FILTER_MAX_IDS = 50;

export enum OrganizationTaskTemplateStatusFilter {
    ACTIVE = "active",
    ARCHIVED = "archived",
    ALL = "all",
}

export enum OrganizationTaskTemplateSortBy {
    TITLE = "title",
    TYPE = "type",
    PRIORITY = "priority",
    GROUP = "group",
    CLOSE_STAGE = "closeStage",
    ASSIGNEE_ROLE = "assigneeRole",
    DEADLINE = "externalDeadline",
    ESTIMATE = "estimatedTime",
    CREATED_AT = "createdAt",
    UPDATED_AT = "updatedAt",
    ARCHIVED_AT = "archivedAt",
}

export enum OrganizationTaskTemplateSortDirection {
    ASC = "asc",
    DESC = "desc",
}

// Query-string serialization collapses a single-element array to a scalar, so
// wrap a lone value back into an array.
const scalarTolerantArray = <TSchema extends z.ZodTypeAny>(
    schema: TSchema,
    maxItems: number,
) =>
    z.preprocess(
        (value) => (typeof value === "string" ? [value] : value),
        z.array(schema).max(maxItems),
    );

export const OrganizationTaskTemplateListQuerySchema = z.object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(ORGANIZATION_TASK_TEMPLATE_LIST_MAX_LIMIT)
        .default(ORGANIZATION_TASK_TEMPLATE_LIST_DEFAULT_LIMIT),
    workspaceId: z.string().uuid().optional(),
    /** @deprecated use `status`; kept for callers that only need active vs all. */
    isArchivedIncluded: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
    status: z.nativeEnum(OrganizationTaskTemplateStatusFilter).optional(),
    search: z.string().trim().max(200).optional(),
    types: scalarTolerantArray(
        z.nativeEnum(TaskType),
        Object.values(TaskType).length,
    ).optional(),
    groupIds: scalarTolerantArray(
        z.string().uuid(),
        ORGANIZATION_TASK_TEMPLATE_FILTER_MAX_IDS,
    ).optional(),
    closeStageIds: scalarTolerantArray(
        z.string().uuid(),
        ORGANIZATION_TASK_TEMPLATE_FILTER_MAX_IDS,
    ).optional(),
    assigneeRoles: scalarTolerantArray(
        z.nativeEnum(TaskTemplateAssigneeRole),
        Object.values(TaskTemplateAssigneeRole).length,
    ).optional(),
    createdByUserIds: scalarTolerantArray(
        z.coerce.number().int(),
        ORGANIZATION_TASK_TEMPLATE_FILTER_MAX_IDS,
    ).optional(),
    sortBy: z.nativeEnum(OrganizationTaskTemplateSortBy).optional(),
    sortDirection: z
        .nativeEnum(OrganizationTaskTemplateSortDirection)
        .optional(),
});

export type OrganizationTaskTemplateListQuery = z.infer<
    typeof OrganizationTaskTemplateListQuerySchema
>;

const sharedFields = {
    description: z.string().max(5000).nullable().optional(),
    priority: z.nativeEnum(TaskPriority).nullable().optional(),
    groupId: z.string().uuid().nullable().optional(),
    closeStageId: z.string().uuid().nullable().optional(),
    assigneeId: z.number().int().nullable().optional(),
    assigneeRole: z.nativeEnum(TaskTemplateAssigneeRole).nullable().optional(),
    externalDeadline: z.number().int().min(0).max(365).nullable().optional(),
    deadlineAnchor: z
        .nativeEnum(TaskTemplateDeadlineAnchor)
        .nullable()
        .optional(),
    estimatedTime: z.number().int().min(0).nullable().optional(),
    initialMessage: z
        .string()
        .max(TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH)
        .nullable()
        .optional(),
    viewConfig: viewConfigValueSchema.nullable().optional(),
};

export const OrganizationTaskTemplateCreateSchema = z
    .object({
        title: z.string().trim().min(1, { message: "Required" }).max(255),
        type: z.nativeEnum(TaskType).nullable().optional(),
        workspaceId: z.string().uuid().nullable().optional(),
        ...sharedFields,
    })
    .superRefine(refineDeadlineAnchor)
    .superRefine(refineViewConfigForCreate);

export type OrganizationTaskTemplateCreate = z.infer<
    typeof OrganizationTaskTemplateCreateSchema
>;

export const OrganizationTaskTemplateUpdateSchema = z
    .object({
        title: z.string().trim().min(1).max(255).optional(),
        type: z.nativeEnum(TaskType).nullable().optional(),
        ...sharedFields,
    })
    .superRefine(refineDeadlineAnchor);

export type OrganizationTaskTemplateUpdate = z.infer<
    typeof OrganizationTaskTemplateUpdateSchema
>;

export const OrganizationTaskTemplateListResponseSchema = z.object({
    taskTemplates: z.array(OrganizationTaskTemplateSchema),
    hasMore: z.boolean(),
    totalCount: z.number().int().min(0),
    serviceTemplateCounts: z.record(z.string().uuid(), z.number().int().min(0)),
});

export const ORGANIZATION_TASK_TEMPLATE_BULK_MAX_IDS = 200;

export const OrganizationTaskTemplateBulkBodySchema = z.object({
    templateIds: z
        .array(z.string().uuid())
        .min(1)
        .max(ORGANIZATION_TASK_TEMPLATE_BULK_MAX_IDS),
});

export type OrganizationTaskTemplateBulkBody = z.infer<
    typeof OrganizationTaskTemplateBulkBodySchema
>;

export const OrganizationTaskTemplateBulkResponseSchema = z.object({
    affectedCount: z.number().int().min(0),
});
